import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    Output
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { IconComponent } from '../icon/icon.component';
import {
    NwTableColumn,
    NwTableRow,
    NwTableRowGroup,
    NwTableCellClickEvent
} from './table.types';

@Component({
    selector: 'nw-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IconComponent, NgTemplateOutlet]
})
export class TableComponent {
    @Input() columns: NwTableColumn[] = [];
    @Input() rows: NwTableRow[] = [];
    @Input({ transform: booleanAttribute }) striped: boolean = true;
    @Input({ transform: booleanAttribute }) showChevron: boolean = false;
    @Input() emptyMessage: string = 'No data available';
    @Input() groupBy?: string;
    @Output() rowClick = new EventEmitter<NwTableRow>();
    @Output() cellClick = new EventEmitter<NwTableCellClickEvent>();

    public trackByColumn(index: number, column: NwTableColumn): string {
        return `${column.key}-${index}`;
    }

    public trackByRow(index: number, row: NwTableRow): string | number {
        return (row['id'] as string | number) ?? index;
    }

    public isMobileHidden(column: NwTableColumn): boolean {
        return !!column.mobileHidden;
    }

    public isTruncated(column: NwTableColumn): boolean {
        return !!column.truncate && column.type !== 'status' && !this.isIcon(column);
    }

    public isIcon(column: NwTableColumn): boolean {
        return column.type === 'icon' && !!column.icon;
    }

    public isClickable(column: NwTableColumn): boolean {
        return !!column.clickable;
    }

    public getCellValue(
        row: NwTableRow,
        column: NwTableColumn
    ): string | number | boolean | null | undefined {
        return row[column.key];
    }

    public getAlignmentClass(align?: NwTableColumn['align']): string {
        switch (align) {
            case 'center':
                return 'nw-table__cell--center';
            case 'end':
                return 'nw-table__cell--end';
            default:
                return 'nw-table__cell--start';
        }
    }

    public getStatusClass(value: unknown): string {
        const status = String(value ?? '')
            .toLowerCase()
            .trim();

        if (status === 'complete') return 'nw-table__status--complete';
        if (status === 'cancelled') return 'nw-table__status--cancelled';
        if (status === 'failed') return 'nw-table__status--failed';
        if (status === 'processing') return 'nw-table__status--processing';
        if (status === 'success') return 'nw-table__status--success';
        if (status === 'received') return 'nw-table__status--received';
        if (status === 'awaiting tax relief')
            return 'nw-table__status--awaiting-tax-relief';
        if (status === 'tax relief received')
            return 'nw-table__status--tax-relief-received';
        if (status === 'new') return 'nw-table__status--new';

        return '';
    }

    public onRowClick(row: NwTableRow): void {
        if (!this.showChevron) {
            return;
        }

        this.rowClick.emit(row);
    }

    public getCellAriaLabel(row: NwTableRow, column: NwTableColumn): string | null {
        const { ariaLabel } = column;

        if (!ariaLabel) {
            return null;
        }

        return typeof ariaLabel === 'function' ? ariaLabel(row) : ariaLabel;
    }

    public onCellClick(row: NwTableRow, column: NwTableColumn, event: Event): void {
        event.stopPropagation();

        this.cellClick.emit({ row, column });
    }

    public get columnCount(): number {
        return (this.columns.length || 1) + (this.showChevron ? 1 : 0);
    }

    public getGroupLabel(row: NwTableRow): string {
        return this.groupBy ? String(row[this.groupBy] ?? '') : '';
    }

    public get rowGroups(): NwTableRowGroup[] {
        if (!this.groupBy) {
            return [{ label: '', rows: [...this.rows], startIndex: 0 }];
        }

        const groups: NwTableRowGroup[] = [];

        this.rows.forEach((row, index) => {
            const label = this.getGroupLabel(row);
            const currentGroup = groups[groups.length - 1];

            if (currentGroup?.label === label) {
                currentGroup.rows.push(row);

                return;
            }

            groups.push({ label, rows: [row], startIndex: index });
        });

        return groups;
    }
}
